import axios from 'axios';

const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:3402/api';

// Create axios instance with auth token
const getAxiosInstance = () => {
  const token = localStorage.getItem('token');
  return axios.create({
    baseURL: API_URL,
    headers: {
      'Content-Type': 'application/json',
      ...(token && { Authorization: `Bearer ${token}` }),
    },
  });
};

export interface StripeConnectAccount {
  id: string;
  chargesEnabled: boolean;
  payoutsEnabled: boolean;
  detailsSubmitted: boolean;
  requirements?: {
    currently_due?: string[];
    eventually_due?: string[];
    past_due?: string[];
    pending_verification?: string[];
  };
}

export interface CreateAccountResponse {
  accountId: string;
  message: string;
}

export interface AccountLinkResponse {
  url: string;
}

export interface AccountStatusResponse {
  account: StripeConnectAccount;
  payoutAccount: {
    id: string;
    stripeConnectAccountId: string;
    chargesEnabled: boolean;
    payoutsEnabled: boolean;
    onboardingComplete: boolean;
    status: string;
  };
}

/**
 * Create a new Stripe Connect Express account
 */
export const createStripeAccount = async (data: {
  country?: string;
  email?: string;
  businessType?: 'individual' | 'company';
}): Promise<CreateAccountResponse> => {
  const api = getAxiosInstance();
  const response = await api.post('/stripe/connect/accounts', data);
  return response.data;
};

/**
 * Generate onboarding URL for Stripe Connect
 */
export const createAccountLink = async (data: {
  accountId: string;
  refreshUrl?: string;
  returnUrl?: string;
}): Promise<AccountLinkResponse> => {
  const api = getAxiosInstance();
  const response = await api.post('/stripe/connect/account-links', data);
  return response.data;
};

/**
 * Get Stripe Connect account status
 */
export const getAccountStatus = async (): Promise<AccountStatusResponse> => {
  const api = getAxiosInstance();
  const response = await api.get('/stripe/connect/account');
  return response.data;
};

/**
 * Disconnect Stripe Connect account
 */
export const disconnectAccount = async (): Promise<{ message: string }> => {
  const api = getAxiosInstance();
  const response = await api.delete('/stripe/connect/account');
  return response.data;
};

/**
 * Initiate Stripe Connect onboarding flow
 */
export const initiateOnboarding = async (data?: {
  country?: string;
  email?: string;
  businessType?: 'individual' | 'company';
}): Promise<string> => {
  try {
    // First, create or get account
    const accountResponse = await createStripeAccount(data || {});
    
    // Then, create account link for onboarding
    const linkResponse = await createAccountLink({
      accountId: accountResponse.accountId,
      refreshUrl: `${window.location.origin}/dashboard/payouts/connect/refresh`,
      returnUrl: `${window.location.origin}/dashboard/payouts/connect/complete`,
    });
    
    return linkResponse.url;
  } catch (error: any) {
    // If account already exists, just create a new link
    if (error.response?.status === 400 && error.response?.data?.message?.includes('already exists')) {
      try {
        const statusResponse = await getAccountStatus();
        const linkResponse = await createAccountLink({
          accountId: statusResponse.payoutAccount.stripeConnectAccountId,
          refreshUrl: `${window.location.origin}/dashboard/payouts/connect/refresh`,
          returnUrl: `${window.location.origin}/dashboard/payouts/connect/complete`,
        });
        return linkResponse.url;
      } catch (retryError) {
        throw error; // Throw original error if retry fails
      }
    }
    throw error;
  }
};

export default {
  createStripeAccount,
  createAccountLink,
  getAccountStatus,
  disconnectAccount,
  initiateOnboarding,
};
